import { styled } from '@mui/material';
import { dividerClasses } from '@mui/material/Divider';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { typographyClasses } from '@mui/material/Typography';
import clsx from 'clsx';
const INVERTED_BORDER = 'color-mix(in srgb, var(--color-tooltip-foreground) 20%, var(--color-tooltip))';
// Tinted so it separates from white pages (raised is #fff in light mode); reads as a lifted surface in dark mode
const NORMAL_BACKGROUND = 'color-mix(in srgb, var(--color-foreground) 1%, var(--color-raised))';

/**
 * Surface + 1px border whose arrow continues the border seamlessly. The arrow is a border-colored diamond (::before)
 * under a surface-colored one (::after) shifted √2px toward the body, leaving a 1px edge on its two outer sides only
 * (a border on the rotated ::before also stroked the inner sides, showing as wedges on the body's border).
 */
function borderedSurface(background, border, foreground) {
  return {
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: background,
      border: `1px solid ${border}`,
      color: foreground
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: border,
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundColor: background
      }
    },
    // Per placement: undo the theme's 1px pull into the body (meant for borderless tooltips) so the arrow starts
    // on the border line instead of poking 1px past it; ::after origins mirror MUI's ::before rules.
    [`&[data-popper-placement*="top"] .${tooltipClasses.arrow}`]: {
      marginBottom: '-0.71em',
      '&::after': {
        transformOrigin: '100% 0',
        transform: `translateY(-${Math.SQRT2}px) rotate(45deg)`
      }
    },
    [`&[data-popper-placement*="bottom"] .${tooltipClasses.arrow}`]: {
      marginTop: '-0.71em',
      '&::after': {
        transformOrigin: '0 100%',
        transform: `translateY(${Math.SQRT2}px) rotate(45deg)`
      }
    },
    [`&[data-popper-placement*="left"] .${tooltipClasses.arrow}`]: {
      marginRight: '-0.71em',
      '&::after': {
        transformOrigin: '0 0',
        transform: `translateX(-${Math.SQRT2}px) rotate(45deg)`
      }
    },
    [`&[data-popper-placement*="right"] .${tooltipClasses.arrow}`]: {
      marginLeft: '-0.71em',
      '&::after': {
        transformOrigin: '100% 100%',
        transform: `translateX(${Math.SQRT2}px) rotate(45deg)`
      }
    }
  };
}
export const CustomTooltip = styled(({
  className,
  color = 'inverted',
  ...props
}) => <Tooltip {...props} classes={{
  popper: clsx(className, `CustomTooltip-${color}`)
}} />)(() => ({
  [`&.CustomTooltip-inverted`]: {
    ...borderedSurface('var(--color-tooltip)', INVERTED_BORDER, 'var(--color-tooltip-foreground)'),
    // Theme surface colors (text.primary, divider) are unreadable on the
    // inverted surface — remap children to the tooltip foreground.
    [`& .${typographyClasses.root}`]: {
      color: 'var(--color-tooltip-foreground)'
    },
    [`& .${dividerClasses.root}`]: {
      borderColor: 'color-mix(in srgb, var(--color-tooltip-foreground) 25%, transparent)',
      '&::before, &::after': {
        borderColor: 'color-mix(in srgb, var(--color-tooltip-foreground) 25%, transparent)'
      }
    }
  },
  [`&.CustomTooltip-normal`]: {
    ...borderedSurface(NORMAL_BACKGROUND, 'var(--color-border)', 'var(--color-foreground)'),
    // Typography follows the (mode-aware) MUI theme; Divider gets the same
    // border token as the tooltip edge for a consistent hairline.
    [`& .${dividerClasses.root}`]: {
      borderColor: 'var(--color-border)',
      '&::before, &::after': {
        borderColor: 'var(--color-border)'
      }
    }
  }
}));